import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import {
  TextInputPassword,
  type TextInputPasswordProps,
} from '../Inputs/TextInputPassword';

export type PasswordFiledProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<TextInputPasswordProps, 'value' | 'defaultValue'>;

/**
 * PasswordField is a wrapper around TextInputPassword component from Mantine library.
 * It is used to create a password input field for react-hook-form library.
 *
 * @example
 * ```tsx
 * <Form>
 *   <PasswordField name="password" />
 * </Form>
 * ```
 *
 * @param name
 * @param control
 * @param defaultValue
 * @param rules
 * @param shouldUnregister
 * @param onChange
 * @param props
 */
export function PasswordField<T extends FieldValues>({
  name,
  control,
  defaultValue,
  rules,
  shouldUnregister,
  onChange,
  ...props
}: PasswordFiledProps<T>) {
  const {
    field: { value, onChange: fieldOnChange, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  return (
    <TextInputPassword
      error={fieldState.error?.message}
      value={value}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(e);
      }}
      {...field}
      {...props}
    />
  );
}
